import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  DISCOVERY_AGE_MIN,
  DISCOVERY_DISTANCE_MAX,
  type DiscoveryEthnicityMode,
  type DiscoveryGenderMode,
} from '@/constants/discoveryFilters';

interface FilterState {
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;

  showIntentions: boolean;
  setShowIntentions: (show: boolean) => void;

  filterDistance: number;
  setFilterDistance: (distance: number) => void;

  filterMinAge: number;
  setFilterMinAge: (age: number) => void;

  filterMaxAge: number;
  setFilterMaxAge: (age: number) => void;

  availableToday: boolean;
  setAvailableToday: (value: boolean) => void;

  /** Show only starred profiles; free, so kept out of `PreferencesSnapshot`. */
  favoritesOnly: boolean;
  setFavoritesOnly: (value: boolean) => void;

  /** Intention ids (e.g. physical, drink) — passed to Firestore array-contains-any. */
  intentions: string[];
  setIntentions: (intentions: string[]) => void;

  openTo: string[];
  setOpenTo: (openTo: string[]) => void;

  relationshipStatus: string[];
  setRelationshipStatus: (statuses: string[]) => void;

  genderMode: DiscoveryGenderMode;
  setGenderMode: (mode: DiscoveryGenderMode) => void;

  ethnicityMode: DiscoveryEthnicityMode;
  setEthnicityMode: (mode: DiscoveryEthnicityMode) => void;

  /** Apply a full preferences snapshot (e.g. Update my preferences). */
  applyPreferences: (prefs: PreferencesSnapshot) => void;

  resetFilters: () => void;
}

export type PreferencesSnapshot = {
  filterDistance: number;
  filterMinAge: number;
  filterMaxAge: number;
  availableToday: boolean;
  intentions: string[];
  openTo: string[];
  relationshipStatus: string[];
  genderMode: DiscoveryGenderMode;
  ethnicityMode: DiscoveryEthnicityMode;
};

export const filterDefaults: PreferencesSnapshot = {
  filterDistance: DISCOVERY_DISTANCE_MAX,
  filterMinAge: DISCOVERY_AGE_MIN,
  filterMaxAge: 100,
  availableToday: false,
  intentions: [],
  openTo: [],
  relationshipStatus: [],
  genderMode: 'all',
  ethnicityMode: 'all',
};

export const useFilterStore = create<FilterState>()(
  persist(
    set => ({
      showFilters: false,
      showIntentions: false,
      favoritesOnly: false,
      ...filterDefaults,
      setShowFilters: show => set({ showFilters: show }),
      setShowIntentions: show => set({ showIntentions: show }),
      setFilterDistance: distance =>
        set({ filterDistance: Math.min(DISCOVERY_DISTANCE_MAX, Math.max(1, distance)) }),
      setFilterMinAge: age => set({ filterMinAge: age }),
      setFilterMaxAge: age => set({ filterMaxAge: age }),
      setAvailableToday: value => set({ availableToday: value }),
      setFavoritesOnly: value => set({ favoritesOnly: value }),
      setIntentions: intentions => set({ intentions }),
      setOpenTo: openTo => set({ openTo }),
      setRelationshipStatus: relationshipStatus => set({ relationshipStatus }),
      setGenderMode: genderMode => set({ genderMode }),
      setEthnicityMode: ethnicityMode => set({ ethnicityMode }),
      applyPreferences: prefs => set({ ...prefs }),
      resetFilters: () => set({ ...filterDefaults }),
    }),
    {
      name: 'pulp-filter-storage',
      version: 4,
      partialize: state => ({
        filterDistance: state.filterDistance,
        filterMinAge: state.filterMinAge,
        filterMaxAge: state.filterMaxAge,
        availableToday: state.availableToday,
        favoritesOnly: state.favoritesOnly,
        intentions: state.intentions,
        openTo: state.openTo,
        relationshipStatus: state.relationshipStatus,
        genderMode: state.genderMode,
        ethnicityMode: state.ethnicityMode,
      }),
      migrate: () => ({
        showFilters: false,
        showIntentions: false,
        favoritesOnly: false,
        ...filterDefaults,
      }),
    }
  )
);
